import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Row, Col, Input, Form } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import PublicLayout from '../comp/layouts/PublicLayout';
import axios from 'axios';
import { trackEvent } from '../controllers/TrackingController';
import CardCheckMessage from './UI/CardCheckMessage';
import { WebAuth } from 'auth0-js';
import { appConfig } from '../appConfig';
import JoinSteps from './UI/JoinSteps';
import { useGetPlan } from '../comp/hooks/query/customer';
import useMessage from '../comp/hooks/useMessage';

function b64DecodeUnicode(str) {
  // Going backwards: from bytestream, to percent-encoding, to original string.
  try {
    return decodeURIComponent(
      window
        .atob(str)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
  } catch {
    return null;
  }
}

const CreatePassword = () => {
  const intl = useIntl();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  const [loading, setLoading] = useState(false);

  const activationToken = useMemo(() => queryParams.get('activationToken'), [queryParams]);
  const activationTokenDecoded = useMemo(
    () => b64DecodeUnicode(activationToken),
    [activationToken]
  );
  const activationParams = useMemo(
    () => JSON.parse(activationTokenDecoded),
    [activationTokenDecoded]
  );
  const { plan, isPlanLoading } = useGetPlan({ planId: activationParams?.plan });

  if (
    !activationTokenDecoded ||
    activationTokenDecoded === 'null' ||
    activationTokenDecoded.expirationDate < Date.now()
  ) {
    return navigate('/join?expiredtoken=warning');
  }

  const onFinish = async (formValues) => {
    const body = {
      activationToken,
      ...formValues,
      ...activationParams,
      isPodcaster: true,
      target: '/',
    };
    delete body.expirationDate;
    delete body.confirmPassword;

    setLoading(true);
    try {
      const result = await axios.post('/api/session/join', body);
      // tracking

      if (!result) {
        throw new Error('An error occurred while creating your account. Please try again later.');
      }

      trackEvent({
        category: 'signup-create-password',
        action: 'form-success',
        label: `join-${activationParams?.plan}`,
      });

      // redirect
      message.success({
        content: <FormattedMessage defaultMessage="Your account has been created" />,
      });

      const webAuth = new WebAuth({
        domain: appConfig.auth0Domain,
        clientID: appConfig.auth0ClientId,
        responseType: 'code',
        reponseMode: 'web_message',
        redirectUri: appConfig.openUrl + '/callback' + window.location.search,
      });

      webAuth.login({
        realm: 'acast-auth',
        email: body.email,
        password: body.password,
      });
    } catch (error) {
      setLoading(false);

      const errMsg = error?.response?.data?.message;
      if (typeof errMsg === 'string') {
        message.danger({
          content: errMsg,
          duration: 3,
          callback: () => navigate('/join?expiredtoken=warning'),
        });
      } else if (error?.response?.status === 400) {
        navigate('/join?expiredtoken=warning');
      } else {
        return message.danger({
          content: (
            <FormattedMessage defaultMessage="We could not create an account at this time" />
          ),
          callback: () => navigate('/join?expiredtoken=warning'),
        });
      }
    }
  };

  const onFinishFailed = () => {
    message.warning({
      content: <FormattedMessage defaultMessage="Please update the required fields" />,
    });
  };

  return (
    <PublicLayout loading={isPlanLoading}>
      <Row
        gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}
        justify="center"
        className="gutter-top-lg public__content"
      >
        <Col xs={24} sm={24} md={12} className="gutter-bottom-lg" style={{ padding: 0 }}>
          <JoinSteps currentStep={1} noCreditCard={plan?.isCreatorNetwork} />
        </Col>
      </Row>
      <Row
        gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}
        justify="center"
        className="gutter-top-lg"
      >
        <Col xs={24} sm={24} md={12} className="gutter-bottom-lg">
          <Form layout="vertical" onFinish={onFinish} onFinishFailed={onFinishFailed}>
            <Row gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}>
              <Col sm={24}>
                <h2>
                  <FormattedMessage
                    defaultMessage="Hi{userFirstName}"
                    values={{
                      userFirstName: activationParams?.firstname
                        ? ` ${activationParams.firstname},`
                        : ',',
                    }}
                  />
                </h2>
              </Col>
            </Row>
            <Row gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }} className="gutter-bottom-lg">
              <Col sm={24}>
                <h3>
                  <FormattedMessage defaultMessage="Please create a password for your account" />
                </h3>
              </Col>
            </Row>
            <Row gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}>
              <Col span={24}>
                <Form.Item
                  help={
                    <small>
                      <FormattedMessage defaultMessage="At least 8 characters, 1 letter, 1 digit & 1 special character" />
                    </small>
                  }
                  name="password"
                  rules={[
                    {
                      type: 'string',
                      message: <FormattedMessage defaultMessage="This is not a valid password." />,
                    },
                    {
                      required: true,
                      message: (
                        <FormattedMessage defaultMessage="Please select a valid password." />
                      ),
                    },
                    {
                      max: 30,
                      message: (
                        <FormattedMessage
                          defaultMessage="Please use fewer than {charactersMax} characters."
                          values={{ charactersMax: '30' }}
                        />
                      ),
                    },
                    {
                      min: 9,
                      message: (
                        <FormattedMessage
                          defaultMessage="Your password should be at least {charactersMin} characters."
                          values={{ charactersMin: '9' }}
                        />
                      ),
                    },
                    {
                      pattern: /\d/,
                      message: <FormattedMessage defaultMessage="Missing 1 digit" />,
                    },
                    {
                      pattern: /\W/,
                      message: <FormattedMessage defaultMessage="Missing 1 special character" />,
                    },
                    {
                      pattern: /[a-zA-Z]/,
                      message: <FormattedMessage defaultMessage="Missing 1 letter" />,
                    },
                  ]}
                >
                  <Input
                    size="large"
                    type="password"
                    prefix={<LockOutlined />}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Password',
                    })}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }} className="gutter-top-md">
              <Col span={24}>
                <Form.Item
                  help={
                    <small>
                      <FormattedMessage defaultMessage="Please re-enter your password" />
                    </small>
                  }
                  name="confirmPassword"
                  rules={[
                    {
                      required: true,
                      message: 'Please confirm your password!',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error('The two passwords that you entered do not match!')
                        );
                      },
                    }),
                  ]}
                >
                  <Input
                    size="large"
                    type="password"
                    prefix={<LockOutlined />}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Confirm Password',
                    })}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row
              gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}
              type="flex"
              justify="space-around"
              align="middle"
              className="gutter-top-lg"
            >
              <Col span={24}>
                <Button
                  block
                  loading={loading}
                  htmlType="submit"
                  size="large"
                  type="primary"
                  shape="round"
                  style={{ fontWeight: 700 }}
                >
                  <FormattedMessage defaultMessage="Continue" />
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      {plan?.isFreePlan && (
        <Row
          gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}
          justify="center"
          className="gutter-top-lg"
        >
          <Col xs={24} sm={24} md={12} className="gutter-bottom-lg">
            <CardCheckMessage plan={plan} />
          </Col>
        </Row>
      )}
    </PublicLayout>
  );
};

export default CreatePassword;
